/**
 * In-session learning overlay.
 *
 * Opens full-screen ABOVE the dashboard without unmounting the live session.
 * Two panes: LEFT is the trail, RIGHT is the real conversation transcript
 * rendered by LearningTranscript (guarded import so this compiles before it
 * exists).
 *
 * The LEFT pane is two tabs over one footer:
 *   Case Record   — ONE Overview card that switches between Causal Learning
 *                   (the diagnostic record) and Informational Learning (the
 *                   session synthesis), then the turn breakdown, then the
 *                   things learned in this session, then "How did it end?"
 *   Expert        — ONE turn-ordered list of the walk and the conversation,
 *                   in List or Graph view, each card keeping its own marks
 * The submit row sits OUTSIDE both, always visible: what is blocking a submit
 * must not be hidden behind the tab that is not open, and the count of steps
 * still owed a mark is repeated on the Expert tab as a badge.
 *
 * The two panes are scroll-synced both ways:
 *   left click  -> `activeTurn` -> transcript scrolls to and flashes that turn
 *   right scroll -> `onTurnInView` -> left pane highlights and reveals the
 *                   matching turn group
 * The transcript owns the loop guard (see LearningTranscript) — it refuses to
 * re-scroll for a turn it reported itself, and ignores observer output while a
 * programmatic scroll is in flight.
 *
 * The overlay shell and the RIGHT transcript pane render IMMEDIATELY on open
 * — those need no server round trip (messages is already in memory).  The
 * LEFT pane shows lightweight skeleton placeholders while the trail loads,
 * never a blank pane with a centered spinner.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, GraduationCap, RefreshCw } from "lucide-react";
import { authFetch } from "../../utils/auth";
import { markSteps, deleteStep as deleteStepApi } from "./useLearning";
import { OutcomeSelector, extractorSuggestedOutcome, stepResultNote } from "./TrailSteps";
import CaseRecord, { LearningEntries } from "./CaseRecord";
import TrailGraph, { TrailViewToggle, TRAIL_VIEWS, useTrailView } from "./TrailGraph";
import { hasDiagnosticMode, isFastOnly, modesLabel } from "./modeLabels";
import TurnBreakdown, { buildTurnRows, rowIsVisible } from "./TurnBreakdown";
import "./styles/scrollbars.css";
import "./styles/informational.css";

/* ── Guarded import of LearningTranscript ───────────────────────────── */
let LearningTranscript = null;
try {
  // Another agent is building this file concurrently. If it does not exist
  // yet the dynamic require fails and we render a placeholder instead.
  // eslint-disable-next-line global-require
  LearningTranscript = require("./LearningTranscript").default;
} catch {
  LearningTranscript = null;
}

/* ── local API helpers ──────────────────────────────────────────────── */
const BASE = "/api/learning";

async function api(path, options) {
  const res = await authFetch(`${BASE}${path}`, options);
  if (res.status === 404) return null;
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail || body.message;
    const msg = typeof detail === "string" ? detail : `Request failed (${res.status})`;
    throw new Error(msg);
  }
  return res.json();
}

/** Read an existing trail for this session (fast — no rebuild). */
const trailBySession = (sessionId) =>
  api(`/trails/by-session/${encodeURIComponent(sessionId)}`);

/** Build/refresh a draft trail from the live session (may be slow). */
const trailFromSession = (sessionId) =>
  api(`/trails/from-session/${encodeURIComponent(sessionId)}`, { method: "POST" });

const patchTrail = (id, body) =>
  api(`/trails/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

const submitTrailApi = (id, outcome) =>
  api(`/trails/${id}/submit`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ outcome }),
  });

/* ── Left pane tabs ─────────────────────────────────────────────────
   The left pane carries TWO tabs over one footer:
     Case Record — the Overview card (Causal ⇄ Informational), the turn
                   breakdown, the things learned, the outcome
     Expert      — the turn-ordered walk and conversation with marks
   BOTH panels stay mounted. Half-typed cause labels, unsaved notes and the
   debounced savers all live in those subtrees, so unmounting the inactive tab
   would silently drop an edit. Hidden, never destroyed — and the same rule is
   why the two Overview views are two mounted cards rather than one card that
   re-reads the trail: switching must not cost anybody what they typed. */
const EMPTY_HYBRID = Object.freeze([]);

/* ── mock affordance ──
   When `?learningMock=1` is in the URL, the informational view is populated
   with mock data so the layout can be iterated without a real informational
   session existing. */
const MOCK_ENABLED =
  typeof window !== "undefined" &&
  new URLSearchParams(window.location.search).get("learningMock") === "1";
let _buildInformationalMock = null;
if (MOCK_ENABLED) {
  try {
    // eslint-disable-next-line global-require
    _buildInformationalMock = require("./mock/informationalMock").buildInformationalMock;
  } catch {
    // mock module not present
  }
}

/* ── Late capture ──
   Capture and extraction run OFF the response path, so a trail can be missing
   (or present but unextracted) for seconds after the turn that made it. The
   overlay waits for them, but only a little and only while it is open — this
   reads a shared production database.
   RETRY_DELAYS_MS  gaps between the reads after a 404: 2s, 4s, 8s (3 reads,
                    ~14s from the first miss), then settle into "nothing yet".
   EXTRACT_REFETCH_MS  ONE further read when the trail is there but the
                    extractor has not filled the case record or turn_state. */
export const RETRY_DELAYS_MS = [2000, 4000, 8000];
export const EXTRACT_REFETCH_MS = 10000;

const hasText = (v) => (typeof v === "string" ? v.trim() !== "" : Array.isArray(v) ? v.length > 0 : !!v);

/** A trail whose steps/turns landed but whose extraction has not. */
function awaitsExtraction(result) {
  const t = result?.trail;
  if (!t) return false;
  const ts = t.turn_state ?? result.turn_state;
  if (Array.isArray(ts) && ts.length > 0) return false;
  return ![
    t.problem_definition, t.symptoms, t.facts, t.possible_causes_active,
    t.possible_causes_ruled_out, t.root_cause, t.resolution, t.summary,
  ].some(hasText);
}

/* The KEY is load-bearing (`lm-tab-case`, the submit-blocker targets, the
   browser tests); only the label reads differently now that the panel holds
   both kinds of learning. */
const LEFT_TABS = [
  { key: "case", label: "Basic" },
  { key: "correction", label: "Expert" },
];

/* ── Skeleton placeholder for the left pane while loading ──────────── */

function StepSkeleton() {
  return (
    <div className="lm-skeleton-steps" aria-hidden="true">
      {/* Symptom skeleton */}
      <div className="lm-skel-block" style={{ width: "40%", height: 12, marginBottom: 6 }} />
      <div className="lm-skel-block" style={{ width: "100%", height: 32, marginBottom: 20 }} />
      {/* Summary skeleton */}
      <div className="lm-skel-block" style={{ width: "50%", height: 12, marginBottom: 6 }} />
      <div className="lm-skel-block" style={{ width: "100%", height: 56, marginBottom: 20 }} />
      {/* 3 step-shaped placeholders */}
      {[0, 1, 2].map((i) => (
        <div key={i} className="lm-skel-step">
          <div className="lm-skel-block" style={{ width: "30%", height: 10, marginBottom: 6 }} />
          <div className="lm-skel-block" style={{ width: "90%", height: 14, marginBottom: 4 }} />
          <div className="lm-skel-block" style={{ width: "60%", height: 10 }} />
        </div>
      ))}
    </div>
  );
}

/* ── component ──────────────────────────────────────────────────────── */

const LearningMode = ({
  sessionId,
  messages = [],
  onClose,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // True while a 404/empty read is still being retried. Distinguishes
  // "capturing" from "nothing to capture" — never true once retries are spent.
  const [waiting, setWaiting] = useState(false);
  const [results, setResults] = useState({});
  /* The optional one-line "why" behind a Ruled out mark, { [step_no]: text }.
     Seeded on load and persisted by the same debounced PATCH as the marks. */
  const [notes, setNotes] = useState({});
  const [outcome, setOutcome] = useState("");
  const [outcomeConfirmed, setOutcomeConfirmed] = useState(false);
  const [activeTurn, setActiveTurn] = useState(null);
  const [busy, setBusy] = useState(false);
  // List vs trace-flow Graph — remembered across screens and reloads.
  const [trailView, setTrailView] = useTrailView();
  // Which tab is showing. Two of them now, and Case Record always opens first.
  const [leftTab, setLeftTab] = useState("case");
  /* The Overview view the engineer is working in, pinned so a refetch cannot
     swap the card out from under them — { id: trailId, view } or null. See
     `activeCaseView` below. */
  const [pinnedCaseView, setPinnedCaseView] = useState(null);
  // Mock data for the Informational tab, active only with ?learningMock=1.
  const mockData = useMemo(
    () => (MOCK_ENABLED && _buildInformationalMock ? _buildInformationalMock() : null),
    []
  );

  /* The case record owns its own state and its own saving (CaseRecord.jsx) —
     the overlay only holds the ref it flushes before submitting, and the
     problem line, which the header echoes so the two can never disagree. Same
     contract as the /learning/{id} review screen. */
  const caseRef = useRef(null);
  /* The informational card saves itself the same way and is flushed the same
     way. Only one of the two is mounted at a time; the other ref is null. */
  const infoCaseRef = useRef(null);
  const [problemLine, setProblemLine] = useState("");

  const [localSteps, setLocalSteps] = useState([]);
  /* Hybrid turn marks and reasons — same pattern as step marks/notes but
     keyed on turn_no. Persisted to a different field but ride the same
     dirty flag so the debounced save covers them. */
  const [hybridMarks, setHybridMarks] = useState({});
  const [hybridReasons, setHybridReasons] = useState({});
  /* Which steps/turns the engineer has explicitly marked in this session.
     Used to distinguish an AI-suggested mark (badge visible) from one the
     engineer has confirmed or set (badge gone). Updated synchronously with
     the mark so the badge disappears instantly, never on refetch. */
  const [confirmedMarks, setConfirmedMarks] = useState({});
  const [confirmedHybrid, setConfirmedHybrid] = useState({});
  const corrections = useMemo(() => data?.corrections || [], [data]);
  const trail = data?.trail;
  const trailId = trail?.id;
  /* The informational half of the Overview card: real data first, mock as a
     dev-only fallback. The causal half always renders the diagnostic view
     regardless; the view itself is picked by intent (see derivedCaseView).
     `entries` is a SIBLING of `trail` in the API payload (same level as
     `steps` and `hybrid`), NOT a property of `trail`. Graft it here so
     CaseRecord sees it on `trail.entries` — this is the ONE place it is read.
     INTENT IS NOT THE GATE. The teaching harvest runs on EVERY session, so a
     diagnostic trail carries entries too and must graft them exactly the same
     way — gating this on intent is what hid a diagnostic session's entries
     while they sat in the very payload this panel had just fetched. `intent`
     is forced to "informational" so CaseRecord renders the synthesis layout
     for a diagnostic trail as well; the causal half does the mirror of this. */
  const infoTrail = useMemo(() => {
    if (trail) {
      return {
        ...trail,
        intent: "informational",
        entries: trail.entries ?? data?.entries ?? [],
      };
    }
    if (MOCK_ENABLED && mockData) return mockData.trail;
    return trail;
  }, [trail, data, mockData]);
  const infoEntries = useMemo(
    () => (Array.isArray(infoTrail?.entries) ? infoTrail.entries : []),
    [infoTrail]
  );
  /* Same gate the old Informational tab used for its empty state: a synthesis
     line, or entries. */
  const hasInformational = infoEntries.length > 0 || !!infoTrail?.about;
  /* WHICH VIEW THE OVERVIEW CARD SHOWS — decided by the session, not by the
     engineer. There is no switch.
       · intent known (trail.intent "informational" / "diagnostic") -> the
         intent wins: "informational" -> Informational, "diagnostic" -> Causal,
         whatever the modes say (a fast-only session that turned diagnostic is
         Causal). The column only ratchets one way, to "diagnostic".
       · intent null / missing / "unknown" (legacy rows, or the classifier has
         not answered yet) -> the old MODE rule: no diagnostic (guided) mode and
         every mode fast/faststage -> Informational; anything else -> Causal.
         modeLabels.js owns the mode strings.
     Either way Informational only when that side has content (an about line
     or entries); otherwise Causal, so the card never opens blank while the
     other side may have content.

     Only the chosen card is MOUNTED (no hidden twin — a hidden duplicate
     toolbar confused `aria-pressed` reads). That is safe because the view can
     only move on its own, on a refetch (late entries, a mode added), and the
     outgoing card would be unmounted with its debounced save cancelled. So the
     view is PINNED the moment the engineer touches the card (focus or pointer
     inside it); from then on refetches never move it, and `saveCase` flushes
     the card that is actually mounted. */
  const caseIntent = trail?.intent;
  const intentKnown =
    caseIntent === "informational" || caseIntent === "diagnostic";
  const leansInformational = intentKnown
    ? caseIntent === "informational"
    : !hasDiagnosticMode(trail?.modes) && isFastOnly(trail?.modes);
  const derivedCaseView =
    leansInformational && hasInformational ? "informational" : "causal";
  const activeCaseView =
    pinnedCaseView && pinnedCaseView.id === trailId
      ? pinnedCaseView.view
      : derivedCaseView;
  const pinCaseView = useCallback(() => {
    setPinnedCaseView((p) =>
      p && p.id === trailId ? p : { id: trailId, view: activeCaseView }
    );
  }, [trailId, activeCaseView]);

  const hybrid = useMemo(() => {
    const h = data?.hybrid;
    return Array.isArray(h) && h.length > 0 ? h : null;
  }, [data]);
  /* Stable identity for the "no hybrid rows" case: TrailGraph rebuilds its
     model whenever this array changes, and a fresh [] per render would make
     that every render. */
  const hybridRows = useMemo(() => hybrid || EMPTY_HYBRID, [hybrid]);
  const turnState = useMemo(() => {
    const ts = data?.trail?.turn_state ?? data?.turn_state;
    return ts !== undefined ? ts : undefined;
  }, [data]);
  const hasTurnState = Array.isArray(turnState) && turnState.length > 0;
  /* The Case Record tab's Turn breakdown renders ONLY when it has a row to
     show. "Not extracted yet" and "No turn data recorded" were a heading over
     an apology; with nothing to say the section is simply absent. Counted with
     the component's own row builder, so "has a row" means the same thing in
     both places — including its rule that a turn with no case-record change
     of its own is not a row on this tab. */
  const hasTurnRows = useMemo(
    () =>
      hasTurnState &&
      buildTurnRows(turnState, [], []).rows.some((r) =>
        rowIsVisible(r, { showDeltas: true, showBodies: false })
      ),
    [hasTurnState, turnState]
  );
  const hasSteps = localSteps.length > 0;
  const hasHybrid = hybrid != null;
  // Track whether marks have been changed by the user (not just loaded).
  const marksDirtyRef = useRef(false);
  const hybridDirtyRef = useRef(false);
  const outcomeTouchedRef = useRef(false);
  const problemTouchedRef = useRef(false);
  /** Apply a trail response to component state. */
  const applyResult = useCallback((result) => {
    setData(result);
    setLocalSteps(result.steps || []);
    /* A refetch can land at any moment (late capture, late extraction, the
       background top-up). What the engineer has already set — the outcome, the
       problem line, step marks and notes — wins over what the server said
       when they asked. Same contract as the hybrid marks below. */
    if (!outcomeTouchedRef.current) {
      // The extractor's suggestion (e.g. "cleared with no change made")
      // outranks capture's inferred outcome but never a human's choice.
      const prefilled =
        extractorSuggestedOutcome(result.trail) ||
        (result.trail?.outcome && result.trail.outcome !== "unknown"
          ? result.trail.outcome
          : "");
      setOutcome(prefilled);
      setOutcomeConfirmed(!prefilled);
    }
    if (!problemTouchedRef.current) {
      // problem_definition only — symptom_text can be walk reasoning; see
      // the `problemFallback={false}` note on the causal CaseRecord below.
      setProblemLine(result.trail?.problem_definition || "");
    }
    const fromServer = Object.fromEntries(
      (result.steps || [])
        .filter((s) => s.result && s.result !== "unknown")
        .map((s) => [s.step_no, s.result])
    );
    // Only the steps that actually carry one; an untouched trail sends none.
    const notesFromServer = Object.fromEntries(
      (result.steps || [])
        .map((s) => [s.step_no, stepResultNote(s)])
        .filter(([, note]) => !!note)
    );
    if (marksDirtyRef.current) {
      setResults((p) => ({ ...fromServer, ...p }));
      setNotes((p) => ({ ...notesFromServer, ...p }));
    } else {
      setResults(fromServer);
      setNotes(notesFromServer);
    }
    /* Hybrid turn marks come back on the hybrid rows themselves. Seeding them
       is what makes a reopened trail show the turns it already marked (and
       makes HybridTrail's "N to mark" count the truth). Guarded by the dirty
       flag for the same reason the step marks are: the background refresh can
       land after the engineer has started marking, and seeding is NOT an edit
       — it must not arm the debounced save. */
    if (!hybridDirtyRef.current) {
      const rows = Array.isArray(result.hybrid) ? result.hybrid : [];
      setHybridMarks(
        Object.fromEntries(
          rows
            .filter((h) => h.result && h.result !== "unknown")
            .map((h) => [h.turn_no, h.result])
        )
      );
      setHybridReasons(
        Object.fromEntries(
          rows.map((h) => [h.turn_no, h.why || ""]).filter(([, why]) => !!why)
        )
      );
    }
    /* No tab is picked from intent any more. The Overview card's view follows
       the session's modes (`activeCaseView`), and is pinned once touched so a
       late refetch never moves the engineer while they are working. */
  }, []);

  /* ── fetch trail data on open ── */
  //
  // StrictMode mounts, cleans up, then remounts the SAME instance. A plain
  // "have I fetched?" ref made the second run bail out while the first run's
  // cleanup had already set cancelled=true — so the response arrived, every
  // setState was skipped, and `loading` stayed true forever. That is the
  // permanent-skeleton bug: the API returned 200 with six steps and the pane
  // never showed them.
  //
  // Sharing the in-flight PROMISE fixes both halves at once: the network call
  // still happens exactly once (no double POST), and whichever mount is alive
  // when it resolves applies the result with its own live flag.
  const fetchRef = useRef(null);
  const refreshedRef = useRef(false);
  const extractRefetchedRef = useRef(false);
  useEffect(() => {
    if (!sessionId) {
      setLoading(false);
      return undefined;
    }
    let active = true;
    const timers = new Set();
    const later = (fn, ms) => {
      const t = setTimeout(() => { timers.delete(t); fn(); }, ms);
      timers.add(t);
      return t;
    };

    /* Trail present, extraction not: ONE delayed read, ever, so the extracted
       fields show up without reopening. Cleanup cancels it, and un-spends the
       one shot if it never ran (StrictMode remount). */
    const undo = [];
    const watchExtraction = (result) => {
      if (extractRefetchedRef.current || !awaitsExtraction(result)) return;
      extractRefetchedRef.current = true;
      let fired = false;
      later(async () => {
        fired = true;
        try {
          const fresh = await trailBySession(sessionId);
          if (active && fresh) applyResult(fresh);
        } catch {
          /* best-effort — the trail is already on screen */
        }
      }, EXTRACT_REFETCH_MS);
      undo.push(() => { if (!fired) extractRefetchedRef.current = false; });
    };

    /* Empty/404: capture has not landed. Read again on a short backoff, stop
       the moment data arrives, and stop for good after the last delay. */
    const retry = (i) => {
      if (!active) return;
      if (i >= RETRY_DELAYS_MS.length) {
        setWaiting(false);
        return;
      }
      later(async () => {
        try {
          const found = await trailBySession(sessionId);
          if (!active) return;
          if (found) {
            applyResult(found);
            setWaiting(false);
            watchExtraction(found);
            return;
          }
        } catch (e) {
          if (!active) return;
          setError(renderError(e));
          setWaiting(false);
          return;
        }
        retry(i + 1);
      }, RETRY_DELAYS_MS[i]);
    };

    if (!fetchRef.current) {
      fetchRef.current = (async () => {
        // Fast READ first; the per-turn capture keeps it current.
        const cached = await trailBySession(sessionId);
        if (cached) return { result: cached, refresh: true };
        // Nothing stored yet — build it from the live session, then fall back
        // to the generic lookup.
        const built = await trailFromSession(sessionId);
        return { result: built || (await trailBySession(sessionId)), refresh: false };
      })();
    }

    setLoading(true);
    setError("");

    fetchRef.current
      .then(async ({ result, refresh }) => {
        if (!active) return;
        if (result) {
          applyResult(result);
          setWaiting(false);
          watchExtraction(result);
        } else {
          setData(null); // module off, or nothing captured yet
          setWaiting(true);
          retry(0);
        }
        setLoading(false);

        // Background top-up so turns walked since the read are picked up.
        // Guarded: both mounts attach to the same promise, and if it had
        // already resolved they would otherwise each fire a refresh.
        if (refresh && !refreshedRef.current) {
          refreshedRef.current = true;
          try {
            const refreshed = await trailFromSession(sessionId);
            if (active && refreshed) {
              applyResult(refreshed);
              watchExtraction(refreshed);
            }
          } catch {
            /* best-effort — a usable trail is already on screen */
          }
        }
      })
      .catch((e) => {
        if (!active) return;
        setError(renderError(e));
        setWaiting(false);
        setLoading(false);
      });

    return () => {
      active = false;
      timers.forEach(clearTimeout);
      timers.clear();
      undo.forEach((u) => u());
    };
  }, [sessionId, applyResult]);

  /* ── auto-save marks (debounced, only when user has changed something) ── */
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!trailId || !marksDirtyRef.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      markSteps(trailId, results, notes).catch(() => {});
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [results, notes, trailId]);

  /* ── auto-save hybrid marks (debounced) ──
     The endpoint exists now, so a rejection is a real failure and is SAID so:
     `set_hybrid_results` writes the batch with one executemany, which means a
     single bad tuple discards every mark in it. Swallowing that left the marks
     painted on screen and stored nowhere. */
  const [hybridError, setHybridError] = useState("");
  const hybridSaveTimer = useRef(null);
  useEffect(() => {
    if (!trailId || !hybridDirtyRef.current) return;
    clearTimeout(hybridSaveTimer.current);
    hybridSaveTimer.current = setTimeout(() => {
      const payload = { results: hybridMarks };
      if (Object.keys(hybridReasons).length) payload.reasons = hybridReasons;
      api(`/trails/${trailId}/hybrid`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      })
        .then(() => setHybridError(""))
        .catch((e) => {
          setHybridError(
            renderError(e) ||
              "Your turn marks could not be saved. They are still on screen — try again."
          );
        });
    }, 1200);
    return () => clearTimeout(hybridSaveTimer.current);
  }, [hybridMarks, hybridReasons, trailId]);

  /* ── the case record ──
     CaseRecord keeps the text and reports its own failures inside the block;
     this only flushes anything outstanding before a submit. It never throws. */
  /* Whichever card is mounted (the other's ref is null -> "noop"), and the
     WORST answer wins: a submit must not go out on top of an edit that could
     not be written. */
  const saveCase = useCallback(async () => {
    const results = await Promise.all([
      caseRef.current ? caseRef.current.flush() : { status: "noop" },
      infoCaseRef.current ? infoCaseRef.current.flush() : { status: "noop" },
    ]);
    const failed = results.find((r) => r?.status === "failed");
    return failed || results.find((r) => r?.status !== "noop") || { status: "noop" };
  }, []);

  /* ── marking ──
     One handler for BOTH views. List mode and graph mode write the same
     `results` map, so a mark made in one is already applied in the other, and
     the debounced markSteps() PATCH above is the single persistence path. */
  const markStep = useCallback((stepNo, value) => {
    marksDirtyRef.current = true;
    setResults((p) => ({ ...p, [stepNo]: value }));
    setConfirmedMarks((p) => ({ ...p, [stepNo]: true }));
  }, []);

  /* A note is a change to the marks, not a thing of its own: it rides the same
     dirty flag and the same debounced PATCH. The empty string is kept in the
     map deliberately — it is how a CLEARED note reaches the backend, which
     deletes the key rather than storing "". */
  const noteStep = useCallback((stepNo, value) => {
    marksDirtyRef.current = true;
    setNotes((p) => ({ ...p, [stepNo]: value }));
  }, []);

  /* ── hybrid turn marking ── */
  const markHybridTurn = useCallback((turnNo, value) => {
    hybridDirtyRef.current = true;
    setHybridMarks((p) => ({ ...p, [turnNo]: value }));
    setConfirmedHybrid((p) => ({ ...p, [turnNo]: true }));
  }, []);

  const noteHybridTurn = useCallback((turnNo, value) => {
    hybridDirtyRef.current = true;
    setHybridReasons((p) => ({ ...p, [turnNo]: value }));
  }, []);

  /* ── suggested count: AI-marked steps/turns awaiting engineer confirmation ── */
  const suggestedCount = useMemo(() => {
    const stepSuggested = localSteps.filter(
      (s) => s.result_source === "inferred" && s.markable !== false && !confirmedMarks[s.step_no]
    ).length;
    const hybridSuggested = (hybrid || []).filter(
      (h) => h.result_source === "inferred" && !confirmedHybrid[h.turn_no]
    ).length;
    return stepSuggested + hybridSuggested;
  }, [localSteps, hybrid, confirmedMarks, confirmedHybrid]);

  /* ── confirm all: accept every AI-suggested mark through the existing endpoints ── */
  const [confirmBusy, setConfirmBusy] = useState(false);
  const confirmAll = useCallback(async () => {
    if (!trailId || suggestedCount === 0) return;
    setConfirmBusy(true);
    setError("");

    const inferredStepNos = localSteps
      .filter((s) => s.result_source === "inferred" && s.markable !== false && !confirmedMarks[s.step_no])
      .map((s) => s.step_no);
    const inferredHybridNos = (hybrid || [])
      .filter((h) => h.result_source === "inferred" && !confirmedHybrid[h.turn_no])
      .map((h) => h.turn_no);

    // Steps: re-post through PATCH .../steps (same as individual marks)
    if (inferredStepNos.length) {
      try {
        await markSteps(trailId, results, notes);
        setConfirmedMarks((p) => {
          const next = { ...p };
          inferredStepNos.forEach((no) => { next[no] = true; });
          return next;
        });
      } catch (e) {
        const msg = e instanceof Error ? e.message : "Could not confirm step marks.";
        setError(msg);
        setConfirmBusy(false);
        return;
      }
    }

    // Hybrid: re-post through PATCH .../hybrid (same as individual marks)
    if (inferredHybridNos.length) {
      try {
        const payload = { results: hybridMarks };
        if (Object.keys(hybridReasons).length) payload.reasons = hybridReasons;
        await api(`/trails/${trailId}/hybrid`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        setConfirmedHybrid((p) => {
          const next = { ...p };
          inferredHybridNos.forEach((no) => { next[no] = true; });
          return next;
        });
        setHybridError("");
      } catch (e) {
        setHybridError(
          e instanceof Error ? e.message : "Could not confirm conversation marks."
        );
      }
    }

    setConfirmBusy(false);
  }, [trailId, suggestedCount, localSteps, hybrid, results, notes, hybridMarks, hybridReasons, confirmedMarks, confirmedHybrid]);

  /* ── step delete ── */
  const [deleteError, setDeleteError] = useState("");
  const removeStep = useCallback(
    async (stepNo) => {
      if (!trailId) return;
      setDeleteError("");
      const prev = localSteps;
      setLocalSteps((s) => s.filter((x) => x.step_no !== stepNo));
      try {
        await deleteStepApi(trailId, stepNo);
      } catch (e) {
        // Revert optimistic removal and surface the real error.
        setLocalSteps(prev);
        const msg = e instanceof Error ? e.message : "Failed to delete step.";
        setDeleteError(msg);
      }
    },
    [trailId, localSteps]
  );

  /* ── submit ── */
  // Only steps that actually ask a question count. The server classifies each
  // step (`markable`) and its submit gate counts the same way — computing this
  // differently here would disable the button on a trail the API would accept.
  // `markable !== false` so a response from an older backend, which has no such
  // field, still behaves as it did before.
  const unmarked = useMemo(
    () =>
      localSteps.filter((s) => s.markable !== false && !results[s.step_no]).length,
    [localSteps, results]
  );

  /* Each blocker knows WHERE it lives. "4 steps are not marked yet" used to
     point at a control 1563px further down the pane with no way to get there,
     and its wording collided with the turn header's "· 4 steps" (which counts
     something else entirely). Now it says what is owed and takes you to it. */
  const submitBlockReasons = useMemo(() => {
    const reasons = [];
    if (unmarked > 0) {
      reasons.push({
        key: "unmarked",
        text: `${unmarked} step${unmarked > 1 ? "s" : ""} still need${unmarked > 1 ? "" : "s"} a mark`,
        target: "[data-unmarked='true']",
        tab: "correction",
      });
    }
    /* AI-suggested marks BLOCK submission until confirmed — nothing reaches
       the learning corpus without a human having actively agreed to it. */
    if (suggestedCount > 0) {
      reasons.push({
        key: "suggested",
        text: `${suggestedCount} AI-suggested mark${suggestedCount > 1 ? "s" : ""} to confirm`,
        target: ".tr-ai-badge",
        tab: "correction",
      });
    }
    if (!outcome) {
      reasons.push({ key: "outcome", text: "Choose an outcome", target: "#lm-outcome", tab: "case" });
    }
    if (outcome && !outcomeConfirmed) {
      reasons.push({ key: "confirm", text: "Confirm the outcome", target: "#lm-outcome", tab: "case" });
    }
    return reasons;
  }, [unmarked, suggestedCount, outcome, outcomeConfirmed]);

  const blockTitle = useMemo(
    () => submitBlockReasons.map((r) => r.text).join(". "),
    [submitBlockReasons]
  );

  /* Scroll the first offender into the middle of the pane and flash it.
     Querying the live scroller beats a ref registry here: the offender may be
     a step card, the outcome row, or (in graph view) neither — a missing
     target simply does nothing rather than throwing. */
  const scrollRef = useRef(null);
  const flashTimer = useRef(null);
  useEffect(() => () => clearTimeout(flashTimer.current), []);
  const doJump = useCallback((selector) => {
    const root = scrollRef.current;
    const el = root?.querySelector(selector);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.remove("tr-flash");
    // force a reflow so re-adding the class restarts the animation
    void el.offsetWidth;
    el.classList.add("tr-flash");
    clearTimeout(flashTimer.current);
    flashTimer.current = setTimeout(() => el.classList.remove("tr-flash"), 1500);
  }, []);

  /* ── left-pane tabs ──
     Both panels are always in the DOM; only the inactive one is [hidden].
     A hidden element has no box, so scrollTop is not readable while it is
     off — the per-tab scroll position is stashed on the way out and put
     back on the way in. Two numbers, no registry. */
  const panelRefs = useRef({});
  const scrollPos = useRef({});
  const pendingJump = useRef(null);

  const selectTab = useCallback((key) => {
    setLeftTab((prev) => {
      if (prev === key) return prev;
      const el = panelRefs.current[prev];
      if (el) scrollPos.current[prev] = el.scrollTop;
      return key;
    });
  }, []);

  useEffect(() => {
    const el = panelRefs.current[leftTab];
    if (el) el.scrollTop = scrollPos.current[leftTab] || 0;
    // A blocker on the other tab: switch first, then scroll to it once the
    // panel actually has a box.
    if (pendingJump.current) {
      const selector = pendingJump.current;
      pendingJump.current = null;
      doJump(selector);
    }
  }, [leftTab, doJump]);

  const jumpTo = useCallback(
    (selector, tab) => {
      if (tab && tab !== leftTab) {
        pendingJump.current = selector;
        selectTab(tab);
        return;
      }
      doJump(selector);
    },
    [leftTab, selectTab, doJump]
  );

  /* Arrow / Home / End move between tabs, as the tablist pattern expects. */
  const onTabKeyDown = useCallback(
    (e) => {
      const i = LEFT_TABS.findIndex((t) => t.key === leftTab);
      let next = null;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % LEFT_TABS.length;
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + LEFT_TABS.length) % LEFT_TABS.length;
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = LEFT_TABS.length - 1;
      if (next === null) return;
      e.preventDefault();
      // Esc still belongs to the overlay — this only claims the arrow keys.
      e.stopPropagation();
      const key = LEFT_TABS[next].key;
      selectTab(key);
      document.getElementById(`lm-tab-${key}`)?.focus();
    },
    [leftTab, selectTab]
  );

  const canSubmit = trailId && submitBlockReasons.length === 0 && !busy;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setBusy(true);
    setError("");
    try {
      if (Object.keys(results).length) await markSteps(trailId, results, notes);
      // Flush hybrid marks. A rejection here stops the submit: the marks are
      // the point of the review, and one discarded batch is the whole batch.
      if (Object.keys(hybridMarks).length) {
        const payload = { results: hybridMarks };
        if (Object.keys(hybridReasons).length) payload.reasons = hybridReasons;
        await api(`/trails/${trailId}/hybrid`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        setHybridError("");
      }
      // Do not submit on top of edits that could not be written.
      const saved = await saveCase();
      if (saved.status === "failed") {
        throw new Error("Your edits could not be saved — nothing was submitted. Try again.");
      }
      await submitTrailApi(trailId, outcome);
      onClose?.();
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Submit failed.";
      setError(msg);
      setBusy(false);
    }
  };

  /* ── Esc to close ── */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  /* ── the overlay owns the viewport while it is open ──
     The two panes are the only scroll containers. Locking the document kills
     the outer page scrollbar, so a wheel over either pane can never move the
     whole overlay as one surface. Restored exactly as found on close. */
  useEffect(() => {
    const { body, documentElement: html } = document;
    const prev = { body: body.style.overflow, html: html.style.overflow };
    body.style.overflow = "hidden";
    html.style.overflow = "hidden";
    return () => {
      body.style.overflow = prev.body;
      html.style.overflow = prev.html;
    };
  }, []);

  /* ── correction submitted from the transcript ── */
  // The first user message is the symptom fallback for the correction composer.
  const firstPrompt = useMemo(() => {
    const first = messages.find((m) => m.sender === "user" || m.sender === "agent");
    return (first?.text || "").trim();
  }, [messages]);

  // When the transcript submits a correction, refresh the trail so the
  // corrections list updates and the turn shows the new correction.
  const handleCorrectionSent = useCallback(() => {
    if (!sessionId) return;
    trailBySession(sessionId)
      .then((result) => { if (result) applyResult(result); })
      .catch(() => { /* best-effort */ });
  }, [sessionId, applyResult]);

  /* ── manual refresh (the button at the end of the tab strip) ──
     Re-reads the STORED trail (GET by-session) — the copy the per-turn
     capture and the extractor keep writing, so it carries the latest
     extraction. It never forces a live capture (`from-session?refresh=true`);
     the open path's own background top-up already covers a trail that is
     behind the live session.

     UNSAVED EDITS ARE FLUSHED FIRST, and a failed flush BLOCKS the refresh
     with a notice: refreshing on top of an edit that could not be written is
     how it would be lost. Marks and notes need nothing extra — applyResult
     keeps what the engineer set over what the server says, and CaseRecord's
     same-trail merge keeps every section touched since load. */
  const [refreshing, setRefreshing] = useState(false);
  const [refreshNotice, setRefreshNotice] = useState("");
  const refreshingRef = useRef(false);
  const handleRefresh = useCallback(async () => {
    if (!sessionId || refreshingRef.current) return;
    refreshingRef.current = true;
    setRefreshing(true);
    setRefreshNotice("");
    try {
      const saved = await saveCase();
      if (saved?.status === "failed") {
        setRefreshNotice("Unsaved edits could not be saved — not refreshed.");
        return;
      }
      const fresh = await trailBySession(sessionId);
      if (fresh) applyResult(fresh);
      else setRefreshNotice("Nothing stored for this session yet.");
    } catch (e) {
      setRefreshNotice(e instanceof Error && e.message ? e.message : "Refresh failed.");
    } finally {
      refreshingRef.current = false;
      setRefreshing(false);
    }
  }, [sessionId, saveCase, applyResult]);

  /* ── scroll sync: right pane reports the turn currently in view ── */
  const handleTurnInView = useCallback((turnNo) => {
    // Re-selecting the same turn stays a no-op (no state churn, no scroll).
    setActiveTurn((prev) => (prev === turnNo ? prev : turnNo));
  }, []);

  /* ── render helpers ── */
  /** Safely render an error value — never stringify a raw object into the DOM. */
  const renderError = (val) => {
    if (!val) return null;
    if (typeof val === "string") return val;
    if (val instanceof Error) return val.message || "An error occurred.";
    if (typeof val === "object") return val.detail || val.message || "Something went wrong.";
    return String(val);
  };

  const symptomDisplay =
    problemLine || trail?.problem_definition || "Learning";

  /* The title used to be a FIFTH copy of the symptom, hard-clipped at the
     pane edge. /learning's table already has the vocabulary that identifies a
     trail — product, mode, step count, status — so the header borrows it and
     leaves the symptom to the case record (and to the tooltip). */
  const headerMeta = useMemo(() => {
    const bits = [];
    if (trail?.product) bits.push(trail.product);
    const modes = modesLabel(trail?.modes);
    if (modes) bits.push(modes);
    if (localSteps.length) {
      bits.push(`${localSteps.length} step${localSteps.length > 1 ? "s" : ""}`);
    }
    return bits;
  }, [trail, localSteps.length]);

  const status = trail?.status || "draft";

  /* Rendered into <body>, not into the dashboard tree: `position: fixed` is
     only viewport-fixed while no ancestor has a transform/filter/contain —
     any of which would silently turn the overlay into a tall in-flow block
     and give the PAGE the scrollbar instead of the panes. React events still
     bubble through the component tree, so nothing else changes. */
  const overlay = (
    <div className="lm-overlay">
      {/* ── header ── */}
      <header className="lm-header">
        <GraduationCap size={16} className="lm-header-icon" />
        <span className="lm-header-title" title={symptomDisplay}>
          Review trail
        </span>
        {headerMeta.length > 0 && (
          <span className="lm-header-meta">{headerMeta.join(" · ")}</span>
        )}
        {data && (
          <span className={`lm-status-pill lm-status-pill--${status}`}>{status}</span>
        )}
        <span className="lm-header-spacer" />
        <button
          type="button"
          className="lm-close-btn"
          onClick={onClose}
          title="Close (Esc)"
        >
          <X size={16} />
        </button>
      </header>

      {/* ── body: two panes — always rendered, never gated on loading ── */}
      <div className="lm-body">
        {/* LEFT PANE: the trail */}
        <section className="lm-left">
          {/* Skeleton while loading (only if we have NO data yet) */}
          {loading && !data && <StepSkeleton />}

          {/* Inline refresh indicator when we have data but are refreshing */}
          {loading && data && (
            <div className="lm-refresh-bar">
              <Loader2 size={13} className="lm-spinner" />
              <span>Refreshing...</span>
            </div>
          )}

          {!loading && error && !data && (
            <div className="lm-center">
              <p className="tr-error-text">{renderError(error)}</p>
            </div>
          )}

          {!loading && !data && !error && waiting && (
            <div className="lm-center" role="status">
              <Loader2 size={20} className="lm-spinner" />
              <p className="lm-center-text">Capturing this session...</p>
            </div>
          )}

          {!loading && !data && !error && !waiting && (
            <div className="lm-center">
              <GraduationCap size={24} style={{ color: "#9ca3af" }} />
              <p className="lm-center-text">
                Nothing captured yet. Keep diagnosing — the trail builds as you go.
              </p>
            </div>
          )}

          {data && (
            /* The strip is the visual row; the tablist inside it holds ONLY
               tabs (ARIA), so the refresh button sits beside it, not in it. */
            <div className="lm-tabstrip">
            <div
              className="flex items-stretch gap-1"
              role="tablist"
              aria-label="Trail review sections"
              onKeyDown={onTabKeyDown}
            >
              {LEFT_TABS.map((tab) => {
                const active = leftTab === tab.key;
                /* Badge for Expert: total outstanding marks (steps + AI
                   suggestions). Nothing else carries one — the entry count
                   went with the tab that used to show it, and it is on the
                   "N things learned" line inside the panel instead. */
                const outstanding = unmarked + suggestedCount;
                let badge = null;
                let badgeTitle = "";
                if (tab.key === "correction" && outstanding > 0) {
                  badge = outstanding;
                  badgeTitle = `${outstanding} step${outstanding > 1 ? "s" : ""} still need${outstanding > 1 ? "" : "s"} a mark`;
                }
                return (
                  <button
                    key={tab.key}
                    id={`lm-tab-${tab.key}`}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    aria-controls={`lm-panel-${tab.key}`}
                    tabIndex={active ? 0 : -1}
                    className={`lm-tab${active ? " lm-tab--active" : ""}`}
                    onClick={() => selectTab(tab.key)}
                  >
                    {tab.label}
                    {badge !== null && (
                      <span className="lm-tab-badge" title={badgeTitle}>
                        {badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
            {refreshNotice && (
              <span
                className="ml-auto self-center truncate pl-2 text-[11px] text-[#b45309]"
                role="status"
                title={refreshNotice}
              >
                {refreshNotice}
              </span>
            )}
            <button
              type="button"
              className={`${refreshNotice ? "ml-2" : "ml-auto"} inline-flex shrink-0 items-center justify-center self-center rounded p-1.5 text-[#6b7280] transition-colors hover:bg-[#f1f5f9] hover:text-[#374151] disabled:cursor-default disabled:opacity-60 disabled:hover:bg-transparent`}
              onClick={handleRefresh}
              disabled={refreshing}
              aria-busy={refreshing}
              title="Refresh"
              aria-label="Refresh"
            >
              <RefreshCw size={14} className={refreshing ? "lm-spinner" : undefined} />
            </button>
            </div>
          )}

          {data && (
            <div className="lm-left-panels" ref={scrollRef}>
            <div
              className="lm-left-scroll"
              role="tabpanel"
              id="lm-panel-case"
              aria-labelledby="lm-tab-case"
              hidden={leftTab !== "case"}
              ref={(el) => { panelRefs.current.case = el; }}
            >
              {/* ── ONE Overview card, ONE view ──
                  The session's modes pick the view (`activeCaseView`); only
                  that CaseRecord is mounted, keyed per view so React never
                  reuses one instance for the other. They stay two components
                  because each owns its own dirty set, baseline and debounced
                  PATCH, and they save through DIFFERENT halves of
                  recordToPatch (it branches on `record.intent`). The wrapper
                  pins the view on first touch — see `pinCaseView`.

                  The causal card ALWAYS renders the diagnostic layout — even
                  for an informational trail — so intent is overridden here.

                  PROBLEM NEVER FALLS BACK TO symptom_text HERE, for ANY
                  intent. Capture fills symptom_text from the walk's symptom
                  NODE REASONING ("User reported slow internet performance
                  while ZCC is active — matches this symptom node directly."),
                  which is the agent talking to itself, not the engineer's
                  problem (live session 84b9e0de). Only problem_definition —
                  Job A's, built from the engineer's own turns — fills PROBLEM;
                  an empty box the engineer fills beats internal reasoning
                  shown as their problem. The trail is left INTACT —
                  `problemFallback` gates the display only, and the save diff
                  runs against a baseline built the same way — so symptom_text
                  is never blanked on the save path, and the embedding and the
                  admin review page (which still falls back) are unaffected.

                  The informational card: `showEntries={false}` — the entries
                  list is not part of the card; it sits below the turn
                  breakdown, where it reads as the outcome of the session. */}
              <div onFocusCapture={pinCaseView} onPointerDownCapture={pinCaseView}>
                {activeCaseView === "informational" ? (
                  <CaseRecord
                    key="informational"
                    ref={infoCaseRef}
                    trail={infoTrail}
                    steps={[]}
                    idPrefix="lm-info"
                    showEntries={false}
                    onSave={(patch) => patchTrail(trailId, patch)}
                  />
                ) : (
                  <CaseRecord
                    key="causal"
                    ref={caseRef}
                    trail={trail?.intent === "informational" ? { ...trail, intent: undefined } : trail}
                    problemFallback={false}
                    steps={localSteps}
                    idPrefix="lm"
                    onSave={(patch) => patchTrail(trailId, patch)}
                    onProblemChange={(v) => { problemTouchedRef.current = true; setProblemLine(v); }}
                  />
                )}
              </div>

              {/* Turn-by-turn breakdown of the case record — shows how
                  the record evolved at each conversation turn. Always
                  rendered, and never switched with the card above: it is the
                  same conversation whichever view of it is open.

                  Steps, Q&A and `turns` are passed for the row TITLES only
                  (`showBodies={false}`): a turn the record did not change is
                  not rendered here, so this heading counts case-record turns
                  and may differ from the Expert tab's — see TurnBreakdown. */}
              {hasTurnRows && (
                <div className="tr-section">
                  <TurnBreakdown
                    turnState={turnState}
                    steps={localSteps}
                    hybrid={hybridRows}
                    turns={data?.turns}
                    showBodies={false}
                  />
                </div>
              )}

              {/* What the session taught, below the walk that produced it.
                  Renders only when there ARE entries — no empty state. With
                  `heading` it is a section shaped like the turn breakdown
                  above: heading row (title, count, Expand all) outside, one
                  white card of rows inside. TrailReview renders the same
                  entries without a heading, inside CaseRecord's own card. */}
              {infoEntries.length > 0 && (
                <div className="tr-section">
                  <LearningEntries entries={infoEntries} heading="Learnings" />
                </div>
              )}

              {/* Outcome — four items on one row. It closes the case write-up,
                  and it stays on THIS tab: the footer hint that asks for it
                  jumps here and switches tabs on the way. */}
              <div className="tr-section tr-outcome-section" id="lm-outcome">
                <p className="tr-section-label">How did it end?</p>
                <OutcomeSelector
                  outcome={outcome}
                  readOnly={false}
                  suggested={
                    extractorSuggestedOutcome(data?.trail) ||
                    (data?.trail?.outcome_source === "inferred"
                      ? data.trail.outcome
                      : null)
                  }
                  onChange={(val) => {
                    outcomeTouchedRef.current = true;
                    setOutcome(val);
                    setOutcomeConfirmed(true);
                  }}
                />
                {outcome && !outcomeConfirmed && (
                  <label className="tr-outcome-confirm">
                    <input
                      type="checkbox"
                      checked={outcomeConfirmed}
                      onChange={(e) => setOutcomeConfirmed(e.target.checked)}
                    />
                    <span>I confirm this outcome is correct</span>
                  </label>
                )}
              </div>

            </div>

            <div
              className="lm-left-scroll"
              role="tabpanel"
              id="lm-panel-correction"
              aria-labelledby="lm-tab-correction"
              hidden={leftTab !== "correction"}
              ref={(el) => { panelRefs.current.correction = el; }}
            >
              {/* ONE turn-ordered list. The walk and the conversation are
                  INDEPENDENT sources — a session can have both (fast +
                  guided), either alone, or neither — but they are the same
                  conversation, so they are read in turn order rather than as
                  two sections a reviewer has to reconcile. The default heading
                  ("Turn breakdown · N turns") is KEPT: TurnBreakdown puts the
                  List/Graph toggle and the graph first and the heading row
                  directly on the row card, so it labels the rows (and carries
                  Expand all) instead of repeating the tab name.

                  TurnBreakdown places the existing cards; it owns neither
                  vocabulary. A step still marks Worked / Ruled out / Can't
                  tell into `results` (PATCH …/steps) and a conversation turn
                  still marks Helpful / Partly / Wrong into `hybridMarks`
                  (PATCH …/hybrid). `turnState` IS passed, with
                  `showDeltas={false}`: the case-record deltas are the Case
                  learning tab's job and repeating them here would say the same
                  thing twice. A turn with only a delta is not rendered here,
                  so this heading counts turns with steps or Q&A and may differ
                  from the Case learning tab's. See TurnBreakdown. */}
              {(hasSteps || hasHybrid) && (
                <>
                  {deleteError && (
                    <p className="tr-error-text" style={{ marginBottom: 6 }}>{renderError(deleteError)}</p>
                  )}
                  {hybridError && (
                    <p className="tr-error-text" style={{ marginBottom: 6 }}>
                      {renderError(hybridError)}
                    </p>
                  )}
                  <TurnBreakdown
                    turnState={turnState}
                    showDeltas={false}
                    variant="review"
                    steps={localSteps}
                    turns={data?.turns}
                    results={results}
                    notes={notes}
                    hybrid={hybridRows}
                    hybridMarks={hybridMarks}
                    hybridReasons={hybridReasons}
                    confirmedMarks={confirmedMarks}
                    confirmedHybrid={confirmedHybrid}
                    readOnly={false}
                    canEdit={true}
                    activeTurn={activeTurn}
                    toolbar={
                      hasSteps || hasHybrid ? (
                        <TrailViewToggle view={trailView} onChange={setTrailView} idPrefix="lm-view" />
                      ) : null
                    }
                    /* On the heading row, beside the toggle — it used to be a
                       right-aligned row of its own above everything. */
                    actions={
                      suggestedCount > 0 ? (
                        <button
                          type="button"
                          className="tr-btn tr-btn--secondary"
                          disabled={confirmBusy}
                          onClick={confirmAll}
                          title={`Accept all ${suggestedCount} AI-suggested mark${suggestedCount > 1 ? "s" : ""} as-is`}
                        >
                          {confirmBusy ? "Confirming..." : `Confirm all ${suggestedCount} AI suggestions`}
                        </button>
                      ) : null
                    }
                    graph={
                      trailView === TRAIL_VIEWS.GRAPH ? (
                        <TrailGraph
                          steps={localSteps}
                          results={results}
                          notes={notes}
                          hybrid={hybridRows}
                          hybridMarks={hybridMarks}
                          hybridReasons={hybridReasons}
                          confirmedMarks={confirmedMarks}
                          confirmedHybrid={confirmedHybrid}
                          readOnly={false}
                          activeTurn={activeTurn}
                          onMark={markStep}
                          onNote={noteStep}
                          onMarkHybrid={markHybridTurn}
                          onReasonHybrid={noteHybridTurn}
                          onStepClick={setActiveTurn}
                        />
                      ) : null
                    }
                    onMarkStep={markStep}
                    onNoteStep={noteStep}
                    onMarkHybrid={markHybridTurn}
                    onReasonHybrid={noteHybridTurn}
                    onTurnClick={setActiveTurn}
                    onRemoveStep={removeStep}
                  />
                </>
              )}

              {!hasSteps && !hasHybrid && (
                <div className="lm-center" style={{ padding: "32px 0" }}>
                  <GraduationCap size={24} style={{ color: "#9ca3af" }} />
                  <p className="lm-center-text">
                    Nothing captured for this conversation yet.
                  </p>
                </div>
              )}

              {/* Corrections */}
              {corrections.length > 0 && (
                <div className="tr-section">
                  <p className="tr-section-label tr-section-label--rose">
                    Reported wrong ({corrections.length})
                  </p>
                  {corrections.map((c) => (
                    <div key={c.id} className="tr-correction">
                      <p className="tr-correction-text">{c.wrong_text}</p>
                      <p className="tr-correction-reason">{c.why_wrong}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          )}

          {/* Footer inside left pane */}
          {data && (
            <div className="lm-left-footer">
              {error && <span className="tr-error-text">{renderError(error)}</span>}
              {submitBlockReasons.length > 0 ? (
                <span className="tr-block-hint">
                  {submitBlockReasons.map((r, i) => (
                    <React.Fragment key={r.key}>
                      {i > 0 && <span className="tr-block-sep">·</span>}
                      <button
                        type="button"
                        className={`tr-block-hint-btn${r.key === "suggested" ? " tr-block-hint-btn--ai" : ""}`}
                        onClick={() => jumpTo(r.target, r.tab)}
                        title={`Go to ${r.text.toLowerCase()}`}
                      >
                        {r.text}
                      </button>
                    </React.Fragment>
                  ))}
                </span>
              ) : (
                <span className="tr-ready-hint">Ready to submit</span>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="tr-btn tr-btn--primary"
                title={blockTitle || undefined}
              >
                {busy ? "Saving..." : "Submit for approval"}
              </button>
            </div>
          )}
        </section>

        {/* RIGHT PANE: the live transcript — always rendered immediately */}
        <section className="lm-right">
          {LearningTranscript ? (
            <LearningTranscript
              messages={messages}
              sessionId={sessionId}
              symptomText={firstPrompt}
              corrections={corrections}
              activeTurn={activeTurn}
              onTurnClick={setActiveTurn}
              onTurnInView={handleTurnInView}
              onCorrectionSent={handleCorrectionSent}
            />
          ) : (
            <div className="lm-center" style={{ color: "#9ca3af" }}>
              <p className="lm-center-text">
                Transcript view is loading...
              </p>
            </div>
          )}
        </section>
      </div>
    </div>
  );

  return typeof document !== "undefined" && document.body
    ? createPortal(overlay, document.body)
    : overlay;
};

export default LearningMode;
